// components/KnowledgeHistory.js
import { useState, useEffect } from 'react'
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot
} from 'firebase/firestore'
import { app } from '../firebase'
import UserService from '../services/UserService'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import CancelIcon from '@mui/icons-material/Cancel'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import './KnowledgeHistory.css'

function KnowledgeHistory ({ knowledgeId, currentUser, userRole }) {
  const [knowledgeHistory, setKnowledgeHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  // Only show for moderators
  const isAdminOrModerator =
    userRole === 'admin' ||
    userRole === 'superadmin' ||
    userRole === 'moderator'

  useEffect(() => {
    if (!knowledgeId || !currentUser || !isAdminOrModerator) {
      setKnowledgeHistory([])
      return
    }

    let unsubscribe = () => {}

    const loadHistory = async () => {
      try {
        setLoadingHistory(true)
        const companyId = await UserService.getUserCompanyId(currentUser.uid)
        const db = getFirestore(app)

        const historyQuery = query(
          collection(
            db,
            'companies',
            companyId,
            'knowledge',
            knowledgeId,
            'history'
          ),
          orderBy('timestamp', 'desc')
        )

        unsubscribe = onSnapshot(historyQuery, snapshot => {
          const history = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          setKnowledgeHistory(history)
          setLoadingHistory(false)
        })
      } catch (error) {
        console.error('Error loading knowledge history:', error)
        setLoadingHistory(false)
      }
    }

    loadHistory()

    return () => unsubscribe()
  }, [knowledgeId, currentUser, isAdminOrModerator])

  // Don't render if not moderator
  if (!isAdminOrModerator) {
    return null
  }

  const getIcon = action => {
    switch (action) {
      case 'technician_submitted':
        return <CheckCircleIcon />
      case 'moderator_edited':
        return <EditIcon />
      case 'moderator_approved':
        return <CheckCircleIcon />
      case 'moderator_rejected':
        return <CancelIcon />
      case 'moderator_reverted':
        return <PlayArrowIcon style={{ transform: 'scaleX(-1)' }} />
      case 'moderator_deleted':
        return <CancelIcon />
      default:
        return <HourglassEmptyIcon />
    }
  }

  const getIconClass = action => {
    switch (action) {
      case 'technician_submitted':
        return 'submitted'
      case 'moderator_edited':
        return 'edited'
      case 'moderator_approved':
        return 'approved'
      case 'moderator_rejected':
        return 'rejected'
      case 'moderator_reverted':
        return 'reverted'
      case 'moderator_deleted':
        return 'deleted'
      default:
        return 'pending'
    }
  }

  const formatTimestamp = timestamp => {
    if (!timestamp) return 'Unbekannt'
    const date = timestamp.toDate
      ? timestamp.toDate()
      : new Date(timestamp)
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className='knowledge-history-container'>
      <h4>Versionshistorie</h4>
      {loadingHistory ? (
        <div className='loading-history'>Lade Historie...</div>
      ) : knowledgeHistory.length === 0 ? (
        <div className='no-history'>
          Noch keine Historie verfügbar
        </div>
      ) : (
        <div className='version-history'>
          {knowledgeHistory.map(event => (
            <div key={event.id} className='history-item'>
              <div className={`history-icon ${getIconClass(event.action)}`}>
                {getIcon(event.action)}
              </div>
              <div className='history-content'>
                <div className='history-title'>
                  {event.actionLabel || event.action}
                </div>
                <div className='history-details'>
                  von {event.userName || 'Unbekannt'}
                  {event.role && ` (${event.role})`}
                  <br />
                  am {formatTimestamp(event.timestamp)}
                  {event.details?.changedFields &&
                    event.details.changedFields.length > 0 && (
                      <>
                        <br />
                        <small>
                          Geänderte Felder:{' '}
                          {event.details.changedFields.join(', ')}
                        </small>
                      </>
                    )}
                  {event.details?.rejectionReason && (
                    <>
                      <br />
                      <small>
                        Grund: {event.details.rejectionReason}
                      </small>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default KnowledgeHistory




